/**
 * Inline message-authoring bar for the unified message history pane.
 *
 * Mirrors the product's composer — a text field with template / attachment /
 * fleet affordances and a mic, plus a send button — but operates on the
 * prototype's demo thread (sending appends a local outbound message). The side
 * affordances are visual only for the prototype.
 */

import AttachFileIcon from "@mui/icons-material/AttachFile";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MicNoneIcon from "@mui/icons-material/MicNone";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";

import { accent, text, zinc } from "../colors";

export function MessageHistoryComposer({ driverName, onSend }: { driverName: string; onSend: (text: string) => void }) {
  const [value, setValue] = useState("");

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onSend(trimmed);
    setValue("");
  };

  return (
    <Box sx={{ flexShrink: 0, borderTop: `1px solid ${zinc[800]}`, p: 1.75, bgcolor: zinc[950] }}>
      <Box
        sx={{
          border: `1px solid ${zinc[700]}`,
          borderRadius: "12px",
          bgcolor: zinc[900],
          px: 1.5,
          py: 1.25,
          maxWidth: 820,
          mx: "auto",
          "&:focus-within": { borderColor: accent.blue },
        }}
      >
        <Box
          component="textarea"
          value={value}
          rows={2}
          placeholder="Start typing your message"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            resize: "none",
            bgcolor: "transparent",
            color: text.primary,
            border: "none",
            outline: "none",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: 1.5,
            caretColor: accent.blue,
            "&::placeholder": { color: text.muted },
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
          <Tooltip title="Templates">
            <IconButton size="small" sx={{ color: text.muted }}>
              <GridViewOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Attach">
            <IconButton size="small" sx={{ color: text.muted }}>
              <AttachFileIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fleet message">
            <IconButton size="small" sx={{ color: text.muted }}>
              <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Record voice message">
            <IconButton size="small" sx={{ color: accent.red }}>
              <MicNoneIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Send to ${driverName} (Enter)`}>
            <span>
              <IconButton
                onClick={submit}
                disabled={value.trim().length === 0}
                size="small"
                sx={{ color: accent.blue, "&.Mui-disabled": { color: zinc[600] } }}
              >
                <SendIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
